/**
 * Shared UI primitives — CreatorHub360 design system
 * Luxury minimal dark theme: #0B0B0B bg, #FFD400 gold accent, white text.
 */

import { type ReactNode, type HTMLAttributes, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ── PageHeader ────────────────────────────────────────────────────────────

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  back?: { label: string; onClick: () => void };
}

export function PageHeader({ title, subtitle, actions, back }: PageHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-4 mb-8">
      <div>
        {back && (
          <button
            onClick={back.onClick}
            className="flex items-center gap-1.5 text-white/40 hover:text-white/80 text-sm mb-3 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {back.label}
          </button>
        )}
        <h1 className="text-2xl font-display font-bold text-white tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-white/50 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

// ── SectionCard ───────────────────────────────────────────────────────────

interface SectionCardProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  noPadding?: boolean;
}

export function SectionCard({ title, subtitle, actions, children, noPadding, className = '', ...rest }: SectionCardProps) {
  return (
    <div className={`card ${className}`} {...rest}>
      {(title || actions) && (
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div>
            {title && <h2 className="text-sm font-semibold text-white uppercase tracking-wider">{title}</h2>}
            {subtitle && <p className="text-xs text-white/40 mt-0.5">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={noPadding ? '' : 'p-5'}>{children}</div>
    </div>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  subtext?: string;
  icon?: ReactNode;
  accent?: boolean;
}

export function StatCard({ label, value, subtext, icon, accent }: StatCardProps) {
  return (
    <div className={`stat-card ${accent ? 'border-gold/20' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-white/40 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-white mt-1.5 truncate">{value}</p>
          {subtext && <p className="text-xs text-white/35 mt-1">{subtext}</p>}
        </div>
        {icon && (
          <div className={`ml-3 p-2.5 rounded ${accent ? 'bg-gold/10 text-gold' : 'bg-white/[0.05] text-white/40'}`}>
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────

export interface EmptyStateProps {
  icon?: ReactNode;
  title?: string;
  message?: string;
  action?: ReactNode;
}

export function EmptyState({ icon, title, message, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      {icon && (
        <div className="w-14 h-14 rounded flex items-center justify-center mb-4 bg-white/[0.04] text-white/30 border border-white/[0.06]">
          {icon}
        </div>
      )}
      {title && <h3 className="text-sm font-semibold text-white uppercase tracking-wider">{title}</h3>}
      {message && <p className="text-sm text-white/40 mt-2 max-w-sm leading-relaxed">{message}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

// ── DataTableSkeleton ─────────────────────────────────────────────────────

export function DataTableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-0">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4 px-5 py-4 border-b border-white/[0.04]">
          {Array.from({ length: cols }).map((_, j) => (
            <div
              key={j}
              className="skeleton h-3.5"
              style={{ width: `${Math.random() * 30 + 15}%`, animationDelay: `${i * 0.05}s` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── StatusBadge ───────────────────────────────────────────────────────────

export type StatusVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'gold';

const statusStyles: Record<StatusVariant, string> = {
  success: 'badge-success',
  warning: 'badge-warning',
  error:   'badge-error',
  info:    'badge-info',
  neutral: 'badge-neutral',
  gold:    'badge-gold',
};

export function StatusBadge({ children, variant = 'neutral' }: { children: ReactNode; variant?: StatusVariant }) {
  return <span className={statusStyles[variant]}>{children}</span>;
}

// ── Toolbar ───────────────────────────────────────────────────────────────

interface ToolbarProps {
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
  filters?: ReactNode;
  actions?: ReactNode;
}

export function Toolbar({ search, filters, actions }: ToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      {search && (
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            value={search.value}
            onChange={e => search.onChange(e.target.value)}
            placeholder={search.placeholder ?? 'Search…'}
            className="input pl-9 py-2 text-sm"
          />
        </div>
      )}
      {filters && <div className="flex items-center gap-2">{filters}</div>}
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  message: string;
  variant?: ToastVariant;
  onDismiss?: () => void;
}

const toastStyles: Record<ToastVariant, string> = {
  success: 'bg-white/[0.05] border-mint-400/20 text-mint-400',
  error:   'bg-white/[0.05] border-red-400/20 text-red-400',
  info:    'bg-white/[0.05] border-gold/20 text-gold',
  warning: 'bg-white/[0.05] border-yellow-400/20 text-yellow-400',
};

export function Toast({ message, variant = 'info', onDismiss }: ToastProps) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -4, scale: 0.97 }}
        transition={{ duration: 0.2 }}
        className={`flex items-center gap-3 px-4 py-3 border text-sm font-medium shadow-xl ${toastStyles[variant]}`}
        style={{ borderRadius: '2px' }}
      >
        <span className="flex-1">{message}</span>
        {onDismiss && (
          <button onClick={onDismiss} className="opacity-50 hover:opacity-100 transition-opacity ml-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

// ── ToastContainer ────────────────────────────────────────────────────────

export interface ToastItem { id: number; message: string; variant: ToastVariant }

export function ToastContainer({ toasts, removeToast, children }: { toasts?: ToastItem[]; removeToast?: (id: number) => void; children?: ReactNode }) {
  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 w-80">
      {toasts && removeToast
        ? toasts.map((t) => <Toast key={t.id} message={t.message} variant={t.variant} onDismiss={() => removeToast(t.id)} />)
        : children}
    </div>
  );
}

// ── useToast hook ─────────────────────────────────────────────────────────

export function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  let counter = 0;

  const addToast = useCallback((message: string, variant: ToastVariant = 'info', duration = 4000) => {
    const id = ++counter;
    setToasts(t => [...t, { id, message, variant }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), duration);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  return { toasts, addToast, removeToast };
}

// ── ScoreBar ──────────────────────────────────────────────────────────────

export function ScoreBar({
  label, score, weight, why, penalty = false,
}: {
  label: string; score: number; weight: number; why: string; penalty?: boolean;
}) {
  const pct = Math.round(score * 100);
  return (
    <div className="mb-3">
      <div className="flex justify-between items-baseline text-xs mb-1.5">
        <span className="font-medium text-white/60 capitalize">
          {label} <span className="text-white/25">×{weight.toFixed(2)}</span>
        </span>
        <span className={`font-bold ${penalty ? 'text-red-400' : 'text-white'}`}>
          {penalty ? '−' : ''}{pct}%
        </span>
      </div>
      <div className="h-1 bg-white/[0.06] overflow-hidden" style={{ borderRadius: '1px' }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className={`h-full ${penalty ? 'bg-red-400/60' : 'bg-gold'}`}
        />
      </div>
      <p className="text-xs text-white/30 mt-1 leading-relaxed">{why}</p>
    </div>
  );
}

// ── ProgressSteps ─────────────────────────────────────────────────────────

export function ProgressSteps({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="flex items-center gap-0">
      {steps.map((step, i) => (
        <div key={step} className="flex items-center">
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 flex items-center justify-center text-xs font-bold transition-all duration-300 ${
              i < current
                ? 'bg-gold/20 text-gold border border-gold/30'
                : i === current
                  ? 'bg-gold text-black'
                  : 'bg-white/[0.05] text-white/30 border border-white/[0.08]'
            }`} style={{ borderRadius: '2px' }}>
              {i < current ? (
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              ) : i + 1}
            </div>
            <span className={`text-xs font-medium hidden sm:block transition-colors ${
              i === current ? 'text-white' : i < current ? 'text-gold/70' : 'text-white/30'
            }`}>
              {step}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`mx-3 h-px flex-1 w-8 transition-colors ${i < current ? 'bg-gold/30' : 'bg-white/[0.06]'}`} />
          )}
        </div>
      ))}
    </div>
  );
}
